<script lang="ts">
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iIcon,
} from '@inscribe/signal';
import { ref } from 'vue';

export default {
  name: 'ExpandableItem',
  components: { iButton, iIcon },
  props: {
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    titleLink: {
      type: String,
    },
    leftIcon: {
      type: String,
    },
    hasRightIcon: {
      type: Boolean,
      default: true,
    },
    rightIcon: {
      type: String,
      default: 'medium-chevron-right',
    },
    iconSize: {
      type: Number,
      default: 24,
    },
    reverseFonts: {
      type: Boolean,
      default: false,
    },
    grayedOut: {
      type: Boolean,
      default: false,
    },
    isClickable: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    return {
      hoverState: ref(false),
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      contentResizeObserver: null,
    };
  },
  mounted() {
    this.setupContentResizeObserver();
  },
  unmounted() {
    this.contentResizeObserver?.disconnect();
  },
  methods: {
    setupContentResizeObserver() {
      const itemElement = this.$el;
      if (!itemElement) return;
      this.contentResizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          this.setDocumentTitleMaxWidth();
        }, 300); // make sure transitions are done
      });
      this.contentResizeObserver.observe(itemElement);
    },
    setDocumentTitleMaxWidth() {
      const itemElement = this.$el;
      if (!itemElement) return;
      const itemElementWidth = itemElement.getBoundingClientRect().width || 0;
      const leftContainer = itemElement.querySelector('.left-container');
      if (!leftContainer) return;
      const leftContainerWidth =
        leftContainer.getBoundingClientRect().width || 0;
      const rightContainer = itemElement.querySelector('.right-container');
      if (!rightContainer) return;
      const rightContainerWidth =
        rightContainer.getBoundingClientRect().width || 0;
      const paddings = 40;
      const maxWidth =
        itemElementWidth - rightContainerWidth - leftContainerWidth - paddings;
      const titleElement = itemElement.querySelector('.text-container-title');
      const subtitleElement = itemElement.querySelector(
        '.text-container-subtitle',
      );
      if (titleElement) {
        titleElement.style.maxWidth = maxWidth + 'px';
      }
      if (subtitleElement) {
        subtitleElement.style.maxWidth = maxWidth + 'px';
      }
    },
    onRightIconClick(evt: Event) {
      evt.stopPropagation();
      this.$emit('on-right-icon-click', evt);
    },
    onItemClick(evt) {
      evt.stopPropagation();
      this.$emit('on-item-click', evt);
    },
    onMouseEnter() {
      if (this.$slots['hover-right-container']) {
        this.hoverState = true;
      }
    },
    onMouseLeave() {
      this.hoverState = false;
    },
  },
};
</script>

<template>
  <button
    :class="{ 'grayed-out': grayedOut, clickable: isClickable }"
    class="expandable-item d-flex align-items-center justify-content-between i-p-8"
    @click="onItemClick"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- left container -->
    <div class="left-container d-flex align-items-center i-mr-8">
      <i-icon
        v-if="leftIcon"
        :symbol="leftIcon"
        :size="iconSize"
        class="left-icon text-neutral"
      />
      <slot name="left-container" />
    </div>

    <!-- text container -->
    <div
      v-if="!$slots.body"
      class="text-container d-flex flex-column align-items-start flex-grow-1 i-gap-1"
    >
      <div
        :class="{
          'text-neutral-bold i-text-button-2': !reverseFonts,
          'text-neutral i-text-body-2': reverseFonts,
        }"
        class="text-container-title"
        data-testid="card-item-title"
      >
        <router-link
          v-if="titleLink"
          :to="titleLink"
          @click="$emit('on-title-link-click')"
          >{{ title }}</router-link
        >
        <template v-else>{{ title }}</template>
      </div>
      <div
        :class="{
          'text-neutral-bold i-text-button-2': reverseFonts,
          'text-neutral i-text-body-2': !reverseFonts,
        }"
        class="text-container-subtitle"
      >
        {{ subtitle }}
      </div>
    </div>
    <div v-else class="d-flex flex-column flex-grow-1">
      <slot name="body" />
    </div>

    <!-- right container -->
    <div class="right-container">
      <div
        :class="{ hidden: hoverState }"
        class="right-container-content d-flex align-items-center"
      >
        <slot name="right-container" />
        <i-button
          v-if="hasRightIcon"
          :right-icon="rightIcon"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          class="i-ml-9"
          @button-click="onRightIconClick"
        />
      </div>
      <div
        :class="{ visible: hoverState }"
        class="hover-right-container-content i-p-6 i-radius-md"
      >
        <slot name="hover-right-container" />
      </div>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.expandable-item {
  border: 0;
  background: var(--base);
  overflow: hidden;
  cursor: default;

  &.clickable {
    cursor: pointer;
  }

  &:hover {
    background: var(--background-neutral);
  }
  &.grayed-out {
    background: var(--surface-neutral-subtle);

    .text-container-title {
      a {
        color: var(--text-neutral);
      }
    }

    .text-container-subtitle {
      color: var(--text-neutral);
    }
  }

  .text-container-subtitle,
  .text-container-title {
    display: block;
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .text-container-title {
    a {
      color: var(--text-neutral-bold);
    }
  }

  .right-container {
    position: relative;

    > div {
      transition:
        opacity 0.3s ease-in-out,
        display 0.3s ease-in-out;
      transition-delay: 0.3s;

      &.hover-right-container-content {
        display: none;
        opacity: 0;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
        background: var(--background-neutral);
      }

      &.visible {
        display: inline-flex;
        opacity: 1;
      }

      &.hidden {
        display: none;
        opacity: 0;
      }
    }
  }
}
</style>
